import React, { useState, useEffect } from 'react';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import User from '../assets/user.png';
import InputField from '../components/ui/InputField';
import { BsBriefcase, BsCalendar2, BsShieldLock } from 'react-icons/bs';
import { FiMapPin, FiLogOut, FiMail, FiUpload } from 'react-icons/fi';
import { MdWork, MdSchool, MdAttachMoney, MdPerson, MdLock } from 'react-icons/md';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BiTime } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchProfile } from '../redux/actions/profileActions';
import { fetchUserProjectsDashboardData } from '../redux/actions/userProjectsAction';
import { logoutUser } from '../redux/actions/authActions';
import Loading from '../components/utils/Loading';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../hooks/usePopup';
import PopupWrapper from '../components/utils/PopupWrapper';
import Toggle from '../components/ui/Toggle';
import toast from 'react-hot-toast';
import { FiUsers } from 'react-icons/fi';
import { GoProject } from 'react-icons/go';
import { RiTeamLine } from 'react-icons/ri';
import {
  MdOutlineSecurity,
  MdAssignmentAdd,
  MdPendingActions,
  MdRateReview,
  MdLocalShipping,
  MdRequestQuote,
  MdPauseCircle,
  MdCancel,
  MdPayments,
  MdRefresh,
} from 'react-icons/md';
import { GoHash } from 'react-icons/go';

const UserProfile = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useAppSelector(state => state.profile);
  const { user } = useAppSelector(state => state.auth);
  const { dashboardData, dashboardDataLoading } = useAppSelector(state => state.userProjects);

  const [isEditable, setIsEditable] = useState(false);
  const [userType, setUserType] = useState<'admin' | 'employee' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Permissions popup state
  const permissionsPopup = usePopup();
  const [userPermissions, setUserPermissions] = useState<any>(null);

  useEffect(() => {
    // Determine user type from Redux state or cookies
    const determineUserType = () => {
      // Check Redux state first
      if (user?.designation === 'superAdmin' || user?.designation === 'admin') {
        setUserType('admin');
      } else if (user?.designation) {
        setUserType('employee');
      } else {
        // Fall back to checking cookies
        const hasAdminCookie = document.cookie.includes('pms_admin_=');
        const hasEmployeeCookie = document.cookie.includes('pms_employee_=');

        if (hasEmployeeCookie) {
          setUserType('employee');
        } else if (hasAdminCookie) {
          setUserType('admin');
        }
      }
    };

    determineUserType();

    try {
      dispatch(fetchProfile())
        .unwrap()
        .then(() => {
          // Profile loaded successfully
          setErrorMessage(null);
        })
        .catch(error => {
          console.error('Error fetching profile:', error);
          setErrorMessage('Failed to load profile data. Please try again.');
        });
    } catch (error) {
      console.error('Error dispatching fetchProfile:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  }, [dispatch, user]);

  // Fetch user dashboard data for project count
  useEffect(() => {
    if (user?.id) {
      // Get current date range (last 30 days for general stats)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      // Fetch dashboard data for employees, and also for admins if they want to see their assigned projects
      if (userType === 'employee' || userType === 'admin') {
        dispatch(
          fetchUserProjectsDashboardData({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
          })
        );
      }
    }
  }, [dispatch, user?.id, userType]);

  // Debug information about the current user type
  useEffect(() => {
    console.log('Current user type:', userType);
    console.log('Auth state user:', user);
    console.log('Profile data:', profileData);
    console.log('Dashboard data:', dashboardData);
    console.log('Total project count from dashboard:', dashboardData?.totalUserProjectsCount);
    if (profileData?.profilePicture) {
      console.log('Profile picture URL:', profileData.profilePicture);
    } else {
      console.log('No profile picture found, using default');
    }
  }, [userType, user, profileData, dashboardData]);

  // Effect to handle profile errors from Redux
  useEffect(() => {
    if (profileError) {
      setErrorMessage(
        `Error: ${typeof profileError === 'string' ? profileError : 'Failed to load profile'}`
      );
    }
  }, [profileError]);

  // Effect to set up permissions when profile data loads
  useEffect(() => {
    if (profileData && profileData.designation) {
      // Check if designation has permissions
      if (typeof profileData.designation === 'object' && profileData.designation.permissions) {
        setUserPermissions({
          entityPermissions: {
            Customer: {
              read: profileData.designation.permissions[0]?.customerRead || false,
              create: profileData.designation.permissions[0]?.customerWrite || false,
              edit: profileData.designation.permissions[0]?.customerEdit || false,
            },
            Project: {
              read: profileData.designation.permissions[0]?.projectRead || false,
              create: profileData.designation.permissions[0]?.projectWrite || false,
              edit: profileData.designation.permissions[0]?.projectEdit || false,
            },
            Employee: {
              read: profileData.designation.permissions[0]?.employeeRead || false,
              create: profileData.designation.permissions[0]?.employeeWrite || false,
              edit: profileData.designation.permissions[0]?.employeeEdit || false,
              manage: profileData.designation.permissions[0]?.canManageEmployee || false,
            },
            Role: {
              read: profileData.designation.permissions[0]?.roleRead || false,
              create: profileData.designation.permissions[0]?.roleWrite || false,
              edit: profileData.designation.permissions[0]?.roleEdit || false,
            },
          },
          workflowPermissions: {
            'Assign Project': {
              enabled: profileData.designation.permissions[0]?.canAssignProject || false,
            },
            'In Progress': {
              enabled: profileData.designation.permissions[0]?.canInProgress || false,
            },
            Review: { enabled: profileData.designation.permissions[0]?.canReview || false },
            'Delivered Project': {
              enabled: profileData.designation.permissions[0]?.canDeliverProject || false,
            },
            'Bill Project': {
              enabled: profileData.designation.permissions[0]?.canBillProject || false,
            },
            'Hold Project': {
              enabled: profileData.designation.permissions[0]?.canHoldProject || false,
            },
            'Cancel Project': {
              enabled: profileData.designation.permissions[0]?.canCancelProject || false,
            },
            'Receive Payment': {
              enabled: profileData.designation.permissions[0]?.canReceivePayment || false,
            },
            'Reopen Project': {
              enabled: profileData.designation.permissions[0]?.canReopenProject || false,
            },
          },
        });
      } else if (user?.designation === 'superAdmin') {
        // SuperAdmin has all permissions
        setUserPermissions({
          entityPermissions: {
            Customer: { read: true, create: true, edit: true },
            Project: { read: true, create: true, edit: true },
            Employee: { read: true, create: true, edit: true, manage: true },
            Role: { read: true, create: true, edit: true },
          },
          workflowPermissions: {
            'Assign Project': { enabled: true },
            'In Progress': { enabled: true },
            Review: { enabled: true },
            'Delivered Project': { enabled: true },
            'Bill Project': { enabled: true },
            'Hold Project': { enabled: true },
            'Cancel Project': { enabled: true },
            'Receive Payment': { enabled: true },
            'Reopen Project': { enabled: true },
          },
        });
      }
    }
  }, [profileData, user]);

  const handleEditClick = async () => {
    if (isEditable) {
      // Save changes
      await handleSaveProfile();
    } else {
      // Enter edit mode
      setEditedData({
        fullname: profileData?.fullname || '',
        email: profileData?.email || '',
      });
      setIsEditable(true);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setEditedData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setSelectedImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      setErrorMessage(null);

      let profilePictureUrl = profileData?.profilePicture;
      let imageChanged = false;

      // Upload image if selected
      if (selectedImage) {
        imageChanged = true;

        // Get pre-signed URL from backend
        const signedUrlResponse = await fetch(
          `/api/v1/admin/s3/presigned-url?contentType=${encodeURIComponent(selectedImage.type)}&folderName=profile-pictures&filename=${encodeURIComponent(selectedImage.name)}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        if (!signedUrlResponse.ok) {
          throw new Error('Failed to get signed URL');
        }

        const signedUrlData = await signedUrlResponse.json();
        const { presignedUrl, fileUrl } = signedUrlData.data;

        // Upload directly to S3 using pre-signed URL
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: selectedImage,
          headers: {
            'Content-Type': selectedImage.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image to S3');
        }

        profilePictureUrl = fileUrl;
      }

      // Check if there are any actual changes
      const currentFullname = editedData.fullname?.trim() || profileData?.fullname || '';
      const currentEmail = editedData.email?.trim() || profileData?.email || '';
      const hasFullnameChange = currentFullname !== (profileData?.fullname || '');
      const hasEmailChange = currentEmail !== (profileData?.email || '');

      // If no changes detected, show info message and exit
      if (!hasFullnameChange && !hasEmailChange && !imageChanged) {
        toast('No changes detected', { icon: 'ℹ️' });
        setIsEditable(false);
        setSelectedImage(null);
        setPreviewImage(null);
        setEditedData({});
        setIsSaving(false);
        return;
      }

      // Update profile data
      const updateData: any = {};
      if (hasFullnameChange && currentFullname) {
        updateData.fullname = currentFullname;
      }
      if (hasEmailChange && currentEmail) {
        updateData.email = currentEmail;
      }
      if (imageChanged && profilePictureUrl) {
        updateData.profilePicture = profilePictureUrl;
      }

      // Only make API call if there are changes
      if (Object.keys(updateData).length > 0) {
        const response = await fetch('/api/v1/admin/profile', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update profile');
        }

        // Refresh profile data
        await dispatch(fetchProfile()).unwrap();

        // Show success message
        toast.success('Profile updated successfully!');
      }

      // Exit edit mode
      setIsEditable(false);
      setSelectedImage(null);
      setPreviewImage(null);
      setEditedData({});
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile. Please try again.');
      setErrorMessage(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      console.log('UserProfile logout initiated');

      // Determine role from user data
      const role = user?.role?.toLowerCase() || 'employee';
      console.log(`Logging out with role: ${role}`);

      // Clear cookies and localStorage first
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');

      // Try to call logout API, but don't wait if it fails
      try {
        await dispatch(logoutUser({ role })).unwrap();
        console.log('Logout API call successful');
      } catch (apiError) {
        console.warn('Logout API call failed, but continuing with client-side logout:', apiError);
      }

      // Navigate to login page
      navigate('/auth/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);

      // Force logout even if there's an error
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');
      navigate('/auth/login', { replace: true });
    }
  };

  // Function to render entity permissions table
  const renderPermissionTable = () => {
    if (!userPermissions) return null;

    const entityIcons: Record<string, React.ReactElement> = {
      Customer: <FiUsers className="text-gray-600" size={18} />,
      Project: <GoProject className="text-gray-600" size={18} />,
      Employee: <RiTeamLine className="text-gray-600" size={18} />,
      Role: <MdOutlineSecurity className="text-gray-600" size={18} />,
    };

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                Entity Permissions
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                Read
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                Create
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                Edit
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(userPermissions.entityPermissions).map(([entity, permissions]) => (
              <>
                <tr key={entity} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-3">
                      {entityIcons[entity]}
                      <span>{entity}</span>
                    </div>
                  </td>
                  {['read', 'create', 'edit'].map(permission => (
                    <td
                      key={`${entity}-${permission}`}
                      className="px-2 py-4 whitespace-nowrap text-center"
                    >
                      <Toggle
                        enabled={permissions[permission] as boolean}
                        onChange={() => { }} // Read-only toggle
                        size="sm"
                        disabled={true}
                      />
                    </td>
                  ))}
                </tr>
                {/* Add Manage Employee row if this is the Employee entity and it has manage permission */}
                {entity === 'Employee' &&
                  permissions &&
                  typeof permissions === 'object' &&
                  'manage' in permissions && (
                    <tr
                      key={`${entity}-manage-row`}
                      className="bg-blue-50 border-l-4 border-blue-400"
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center gap-3 ml-6">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          </div>
                          <span className="font-medium">Manage Employee</span>
                          <span className="text-xs text-gray-500">(Advanced permission)</span>
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                        <span className="text-gray-400">—</span>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                        <div className={!(permissions as any).read ? 'opacity-50' : ''}>
                          <Toggle
                            enabled={Boolean((permissions as any).manage)}
                            onChange={() => { }} // Read-only toggle
                            size="sm"
                            disabled={true}
                          />
                        </div>
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                        <span className="text-gray-400">—</span>
                      </td>
                    </tr>
                  )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Function to render workflow permissions table
  const renderWorkflowTable = () => {
    if (!userPermissions) return null;

    const workflowIcons: Record<string, React.ReactElement> = {
      'Assign Project': <MdAssignmentAdd className="text-gray-600" size={18} />,
      'In Progress': <MdPendingActions className="text-gray-600" size={18} />,
      Review: <MdRateReview className="text-gray-600" size={18} />,
      'Delivered Project': <MdLocalShipping className="text-gray-600" size={18} />,
      'Bill Project': <MdRequestQuote className="text-gray-600" size={18} />,
      'Hold Project': <MdPauseCircle className="text-gray-600" size={18} />,
      'Cancel Project': <MdCancel className="text-gray-600" size={18} />,
      'Receive Payment': <MdPayments className="text-gray-600" size={18} />,
      'Reopen Project': <MdRefresh className="text-gray-600" size={18} />,
    };

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Workflow Permissions
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Access
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(userPermissions.workflowPermissions).map(([workflow, workflowData]) => {
              const { enabled } = workflowData as { enabled: boolean };
              return (
                <tr key={workflow} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-3">
                      {workflowIcons[workflow]}
                      <span>{workflow}</span>
                    </div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-center">
                    <Toggle
                      enabled={Boolean(enabled)}
                      onChange={() => { }} // Read-only toggle
                      size="sm"
                      disabled={true}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (profileLoading) {
    return <Loading height="calc(100vh - 100px)" />;
  }

  if (errorMessage) {
    return (
      <ComponentWrapper title={'Profile Error'}>
        <div className="flex flex-col items-center justify-center p-10 bg-white rounded-lg shadow">
          <div className="text-red-500 text-xl mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {errorMessage}
          </div>
          <button
            onClick={() => dispatch(fetchProfile())}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </ComponentWrapper>
    );
  }

  if (!profileData) {
    return (
      <ComponentWrapper title={'Profile'}>
        <div className="text-center p-10 bg-white rounded-lg shadow">
          No profile data available.
        </div>
      </ComponentWrapper>
    );
  }

  // Get designation safely - this is a critical part that was causing the error
  const getDesignationDisplay = () => {
    try {
      if (!profileData.designation) return 'N/A';

      // Check if designation is an object with a name property
      if (typeof profileData.designation === 'object' && profileData.designation !== null) {
        if ('name' in profileData.designation) {
          return String(profileData.designation.name).toUpperCase();
        }
        // If it's an object but doesn't have a name property, return placeholder
        return 'Unknown Designation';
      }

      // If it's a primitive value like string, return it directly
      return String(profileData.designation).toUpperCase();
    } catch (err) {
      console.error('Error processing designation:', err);
      return 'N/A';
    }
  };

  const designationDisplay = getDesignationDisplay();

  // Get ID to display (employeeId for employees, adminID for admins)
  const idPrefix = userType === 'employee' ? 'E' : 'A';
  const idValue = userType === 'employee' ? profileData.employeeId : profileData.adminID || '';

  // Access projects data safely - use dashboard data for total count, fallback to profile projects
  const projectData = Array.isArray(profileData.projects) ? profileData.projects : [];
  const totalProjectCount =
    userType === 'employee' || userType === 'admin'
      ? (dashboardData?.totalUserProjectsCount ?? projectData.length)
      : projectData.length;

  return (
    <ComponentWrapper title={'Profile'}>
      <div className="bg-white rounded-lg p-4 flex justify-between items-center mb-4">
        <div className="flex items-center">
          <div className="relative group">
            <img
              src={previewImage || profileData.profilePicture || User}
              alt="Profile"
              className="w-24 h-24 rounded-lg object-cover"
              onError={e => {
                console.error('Profile picture failed to load:', profileData.profilePicture);
                e.currentTarget.src = User;
              }}
              onLoad={() => {
                console.log('Profile picture loaded successfully:', profileData.profilePicture);
              }}
            />
            {isEditable && userType === 'admin' && (
              <label
                htmlFor="profile-image-upload"
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg"
              >
                <FiUpload className="text-white text-2xl" />
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
            {idValue && (
              <span className="absolute translate-y-[50%] bottom-0 bg-white text-gray-500 text-md px-2 rounded-full left-1/2 transform -translate-x-1/2">
                {idPrefix}
                {idValue}
              </span>
            )}
          </div>
          <div className="ml-8">
            <div className="flex items-center pb-2">
              <h1 className="text-4xl font-bold">{profileData.fullname}</h1>
              <span className="ml-3 bg-blue-100 text-blue-600 text-sm font-medium px-3 py-1 rounded-full self-end mb-1">{designationDisplay}</span>
            </div>
            {userType === 'employee' && profileData.manager && (
              <div className="flex items-center text-gray-500">
                <span className="text-red-500 font-medium">Manager:</span>
                <span className="ml-2 text-gray-700 font-semibold">
                  {profileData.manager.fullname}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            className="flex items-center border border-gray-300 rounded-lg px-6 py-3 text-gray-800 hover:bg-gray-50 hover:shadow-md transition-shadow"
            onClick={permissionsPopup.openPopup}
          >
            <BsShieldLock className="h-5 w-5 mr-2" />
            View Permissions
          </button>
          <button
            className="bg-red-600 text-white rounded-lg px-6 py-3 flex items-center hover:bg-red-700 hover:shadow-md transition-shadow"
            onClick={handleLogout}
          >
            <FiLogOut className="h-5 w-5 mr-2" />
            Logout
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Total Projects Card */}
        <div className="bg-blue-50/50 rounded-lg p-6 flex items-center hover:bg-blue-50 transition-colors">
          <div className="bg-blue-100 p-4 rounded-full">
            <HiOutlineDocumentText className="h-6 w-6 text-blue-500" />
          </div>
          <div className="ml-6">
            <h2 className="text-xl text-blue-600 font-semibold">Total Projects</h2>
            {dashboardDataLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                <p className="text-lg text-blue-500">Loading...</p>
              </div>
            ) : (
              <p className="text-3xl font-bold text-blue-700">{totalProjectCount}</p>
            )}
          </div>
        </div>

        {/* Work Hours Card */}
        <div className="bg-gray-50 rounded-lg p-6 flex items-center cursor-not-allowed">
          <div className="bg-gray-100 p-4 rounded-full">
            <BiTime className="h-6 w-6 text-gray-500" />
          </div>
          <div className="ml-6">
            <h2 className="text-xl text-gray-600 font-semibold">Work Hours</h2>
            <p className="text-3xl font-bold text-gray-700">0.0 hrs</p>
          </div>
        </div>
      </div>

      {/* User Details Card */}
      <div className="bg-white rounded-lg p-6 mb-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-700">User Details</h2>
          {userType !== 'employee' && (
            <button
              className={`border rounded-full px-4 py-1 flex items-center transition-shadow ${isSaving
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : isEditable
                  ? 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                  : 'text-gray-700 hover:shadow-md'
                }`}
              onClick={handleEditClick}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  {isEditable ? 'SAVE' : 'EDIT'}
                </>
              )}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <InputField
            label="Full Name"
            value={isEditable ? editedData.fullname : profileData.fullname}
            onChange={e => handleInputChange('fullname', e.target.value)}
            icon={<MdPerson />}
            disabled={!isEditable}
            labelClassName="text-gray-500"
          />

          {/* Email */}
          <InputField
            label="Email"
            value={isEditable ? editedData.email : profileData.email}
            onChange={e => handleInputChange('email', e.target.value)}
            icon={<FiMail />}
            disabled={!isEditable}
            labelClassName="text-gray-500"
          />

          {/* Job Location */}
          {profileData.jobLocation && (
            <InputField
              label="Job Location"
              value={profileData.jobLocation}
              onChange={e => handleInputChange('jobLocation', e.target.value)}
              icon={<FiMapPin />}
              disabled={!isEditable}
              labelClassName="text-gray-500"
            />
          )}

          {/* Employee-specific fields */}
          {userType === 'employee' && (
            <>
              {profileData.dob && (
                <InputField
                  label="Date of Birth"
                  value={new Date(profileData.dob).toLocaleDateString()}
                  onChange={e => handleInputChange('dob', e.target.value)}
                  icon={<BsCalendar2 />}
                  disabled={!isEditable}
                  labelClassName="text-gray-500"
                />
              )}

              {profileData.dateOfJoining && (
                <InputField
                  label="Date of Joining"
                  value={new Date(profileData.dateOfJoining).toLocaleDateString()}
                  onChange={e => handleInputChange('dateOfJoining', e.target.value)}
                  icon={<BsCalendar2 />}
                  disabled={!isEditable}
                  labelClassName="text-gray-500"
                />
              )}

              {profileData.education && (
                <InputField
                  label="Education"
                  value={profileData.education}
                  onChange={e => handleInputChange('education', e.target.value)}
                  icon={<MdSchool />}
                  disabled={!isEditable}
                  labelClassName="text-gray-500"
                />
              )}

              {profileData.employeeStatus && (
                <InputField
                  label="Status"
                  value={profileData.employeeStatus}
                  onChange={e => handleInputChange('employeeStatus', e.target.value)}
                  icon={<BsBriefcase />}
                  disabled={!isEditable}
                  labelClassName="text-gray-500"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Address Information for Employees */}
      {userType === 'employee' && profileData.address && profileData.address.length > 0 && (
        <div className="bg-white rounded-lg p-6 mb-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-700">Address Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Address"
              value={profileData.address[0].address}
              onChange={e => handleInputChange('address', e.target.value)}
              icon={<FiMapPin />}
              disabled={!isEditable}
              labelClassName="text-gray-500"
            />

            <InputField
              label="City"
              value={profileData.address[0].city}
              onChange={e => handleInputChange('city', e.target.value)}
              icon={<FiMapPin />}
              disabled={!isEditable}
              labelClassName="text-gray-500"
            />

            <InputField
              label="State"
              value={profileData.address[0].state}
              onChange={e => handleInputChange('state', e.target.value)}
              icon={<FiMapPin />}
              disabled={!isEditable}
              labelClassName="text-gray-500"
            />

            <InputField
              label="Pin Code"
              value={profileData.address[0].pinCode.toString()}
              onChange={e => handleInputChange('pinCode', e.target.value)}
              icon={<FiMapPin />}
              disabled={!isEditable}
              labelClassName="text-gray-500"
            />
          </div>
        </div>
      )}

      {/* Manager Information for Employees */}
      {userType === 'employee' && profileData.manager && (
        <div className="bg-white rounded-lg p-6 mb-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-700">Manager Information</h2>
          </div>

          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              <img
                src={profileData.manager.profilePicture || User}
                alt={profileData.manager.fullname}
                className="w-16 h-16 rounded-lg object-cover"
                onError={e => {
                  console.error(
                    'Manager profile picture failed to load:',
                    profileData.manager.profilePicture
                  );
                  e.currentTarget.src = User;
                }}
                onLoad={() => {
                  console.log(
                    'Manager profile picture loaded successfully:',
                    profileData.manager.profilePicture
                  );
                }}
              />
            </div>
            <div className="flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Manager Name"
                  value={profileData.manager.fullname}
                  onChange={() => { }}
                  icon={<MdPerson />}
                  disabled={true}
                  labelClassName="text-gray-500"
                />

                <InputField
                  label="Manager ID"
                  value={`E${profileData.manager.employeeId}`}
                  onChange={() => { }}
                  icon={<GoHash />}
                  disabled={true}
                  labelClassName="text-gray-500"
                />

                <InputField
                  label="Manager Designation"
                  value={profileData.manager.designation?.name || 'N/A'}
                  onChange={() => { }}
                  icon={<MdWork />}
                  disabled={true}
                  labelClassName="text-gray-500"
                />

                <InputField
                  label="Email"
                  value={profileData.manager.email}
                  onChange={() => { }}
                  icon={<FiMail />}
                  disabled={true}
                  labelClassName="text-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Projects Card */}
      {projectData.length > 0 && (
        <div className="bg-white rounded-lg p-6">
          <h2 className="text-xl font-bold text-green-600 mb-6">Recent Projects</h2>

          {projectData.map((project: any) => (
            <div
              key={project.id}
              className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4"
            >
              <div className="flex flex-col md:flex-row md:items-center mb-2">
                <div className="bg-green-100 text-green-600 text-sm rounded-full px-3 py-1 mb-2 md:mb-0 inline-block w-max">
                  {project.projectId}
                </div>
                <div className="text-green-500 text-sm md:ml-4">
                  Received on : {project.receivedDate}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between mt-2">
                <h3 className="text-lg font-semibold text-gray-800">{project.clientName}</h3>
                <div className="flex items-center mt-2 md:mt-0">
                  <span className="font-semibold text-gray-700">{project.projectCode}</span>
                  <span className="mx-4 text-gray-300">|</span>
                  <span className="font-semibold text-gray-700">{project.projectName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permissions Popup */}
      <PopupWrapper
        title={
          <div className="flex flex-col gap-1">
            <span className="text-2xl">User Permissions</span>
            <span className="text-sm text-gray-500">Permissions for {designationDisplay}</span>
          </div>
        }
        isOpen={permissionsPopup.isOpen}
        onClose={permissionsPopup.closePopup}
        size="lg"
        logo={<MdOutlineSecurity size={53} />}
      >
        <div className="flex flex-col py-6">
          {userPermissions ? (
            <>
              {renderPermissionTable()}
              {renderWorkflowTable()}

              <div className="text-center text-gray-500 mt-6">
                <p>This is a read-only view of your permissions based on your designation.</p>
                {user?.designation === 'superAdmin' && (
                  <p className="mt-2 font-medium text-blue-600">
                    As a SuperAdmin, you have full access to all system features.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center ">
              <Loading height="400px" />
            </div>
          )}
        </div>
      </PopupWrapper>
    </ComponentWrapper>
  );
};

export default UserProfile;
